import { MantineColorsTuple } from '@mantine/core'

/**
 * Paleta Doclenz.
 *
 * Ancorada nas cores medidas do próprio logo — o gradiente da marca vai de
 * `#2757c9` (azul, à esquerda) a `#1ec4d4` (turquesa, à direita), amostrados dos pixels de
 * `logo_doclenz_single.png`. Não foram escolhidos de memória.
 *
 * O tom 6 é o `filled` — a cor efetiva de botões, barra lateral e destaques. Ele é
 * deliberadamente **mais escuro** que o azul da marca: a marca precisa brilhar num fundo branco,
 * a interface precisa de uma primária que não cause fadiga em uso prolongado nem compita com o
 * conteúdo. O azul da marca fica no tom 5, um passo acima.
 */
export const doclenzBlue: MantineColorsTuple = [
  '#eef4fc',
  '#d6e4f7',
  '#adc7ee',
  '#7ea6e2',
  '#5285d4',
  '#2a63c4', // azul da marca
  '#1f4fa5', // ← primária
  '#1a4188',
  '#14316a',
  '#0d2049',
]

/** Acento: a ponta turquesa do gradiente da marca. Usado em realces e no gradiente do logo. */
export const doclenzTeal: MantineColorsTuple = [
  '#e6fafb',
  '#c5f3f6',
  '#93e8ee',
  '#5bdae4',
  '#2ecbd8',
  '#1ec4d4', // turquesa da marca
  '#17a6b6',
  '#128594',
  '#0e6672',
  '#0a4a53',
]

/** Neutros levemente azulados: os cinzas puxam para o azul, não para o verde. */
export const doclenzGray: MantineColorsTuple = [
  '#f7f9fc',
  '#eef2f8',
  '#e2e8f1',
  '#c7d1e0',
  '#9daabf',
  '#66748c',
  '#4a5670',
  '#2f3950',
  '#1b2337',
  '#101728',
]

/** Tokens literais, para CSS e estilos inline — onde o tema do Mantine não alcança. */
export const DoclenzTokens = {
  navy950: '#081735',
  blue900: '#0d2049',
  blue800: '#14316a',
  blue700: '#1a4188',
  blue600: '#1f4fa5',
  blue500: '#2a63c4',
  blue400: '#5285d4',
  teal: '#1ec4d4',

  light: {
    bg: '#f4f7fc',
    surface: '#ffffff',
    surface2: '#f8fafd',
    surface3: '#eef3fa',
    text: '#101728',
    muted: '#66748c',
    border: '#e2e8f1',
    shadow: '0 10px 30px rgba(13,32,73,.08)',
  },
  dark: {
    bg: '#080e1c',
    surface: '#0e1730',
    surface2: '#101c3a',
    surface3: '#172647',
    text: '#eaf1fb',
    muted: '#9daabf',
    border: '#23335c',
    shadow: '0 12px 36px rgba(0,0,0,.28)',
  },

  /**
   * Ícones do menu lateral: azul acinzentado, não a primária.
   *
   * O turquesa da marca sobre o fundo escuro fica neon e compete com o item ativo; este tom
   * quebra o bloco de branco sem chamar atenção. O rótulo segue branco, então a hierarquia
   * texto > ícone se mantém.
   */
  sidebarIcon: '#9daabf',

  /** Gradiente da barra lateral: azul-marinho quase preto descendo para azul profundo. */
  sidebarGradient: 'linear-gradient(180deg, rgba(8,23,53,.98), rgba(20,49,106,.97))',

  /** Fundo do item de menu ativo. */
  sidebarItemActive: 'linear-gradient(90deg, rgba(42,99,196,.88), rgba(31,79,165,.72))',

  /**
   * Fundo da área de conteúdo: dois halos radiais sobre a cor base — um turquesa no alto à
   * direita, um azul embaixo à esquerda, ecoando o gradiente da marca. Multi-camada na
   * shorthand `background` (gradientes primeiro, cor por último).
   *
   * A prop `mainBackground` do layout é um valor único, não um par claro/escuro — quem escolhe
   * entre as duas variantes é o `colorScheme` no App.
   */
  mainBackground:
    'radial-gradient(circle at 85% 8%, rgba(30,196,212,.10), transparent 26%),' +
    'radial-gradient(circle at 15% 88%, rgba(42,99,196,.07), transparent 28%),' +
    '#f4f7fc',
  mainBackgroundDark:
    'radial-gradient(circle at 85% 8%, rgba(30,196,212,.08), transparent 26%),' +
    'radial-gradient(circle at 15% 88%, rgba(42,99,196,.06), transparent 28%),' +
    '#080e1c',

  /**
   * Cores do tab strip (`ArchbaseAdminTabContainer`, archbase ≥ 4.0.45).
   * A aba ativa fica na cor da superfície do conteúdo — mantendo a leitura de
   * "a aba ativa é a folha aberta embaixo" — e ganha um traço da primária no
   * topo. A faixa usa `surface3` para separar do conteúdo sem virar um bloco.
   */
  tabs: {
    barBackground: '#eef3fa',
    tabBackground: '#f8fafd',
    tabActiveBackground: '#ffffff',
    titleColor: '#66748c',
    titleActiveColor: '#1f4fa5',
    activeAccentColor: '#1f4fa5',
    dividerColor: '#c7d1e0',
    bottomBarBackground: '#ffffff',
  },
  tabsDark: {
    barBackground: '#101c3a',
    tabBackground: '#0e1730',
    tabActiveBackground: '#172647',
    titleColor: '#9daabf',
    titleActiveColor: '#5285d4',
    activeAccentColor: '#1ec4d4',
    dividerColor: '#23335c',
    bottomBarBackground: '#172647',
  },

  /** Gradiente do botão primário. */
  primaryGradient: 'linear-gradient(135deg, #2a63c4, #1a4188)',
  /** Gradiente da marca — a mesma direção do logo: azul para turquesa. */
  brandGradient: 'linear-gradient(135deg, #2757c9, #1ec4d4)',
} as const
